/**
 * React Query hooks for fetching festival data from the async API.
 *
 * Replaces direct static import of the 6400-line festival-data/index.ts registry
 * for consumers that don't need the full data at module load time.
 *
 * The hooks use long staleTime / gcTime since festival data changes at most
 * a few times per year — no need to re-fetch on every mount.
 */

import { useQuery } from "@tanstack/react-query";
import type { FestivalRegistryEntry } from "@/festival-data";
import type { FestivalConfig } from "@/types/festival";

export interface UseFestivalRegistryOptions {
  country?: string;
  category?: string;
  month?: number;
  q?: string;
  destination?: string;
  enabled?: boolean;
}

/**
 * Fetch the full (or filtered) festival registry from the server.
 *
 * @param opts - Optional filters: country, category, month (1-12), fuzzy q
 * @returns useQuery result with `data: FestivalRegistryEntry[]`
 */
export function useFestivalRegistry(opts?: UseFestivalRegistryOptions) {
  const { country, category, month, q, destination, enabled = true } = opts ?? {};

  const params = new URLSearchParams();
  if (country) params.set("country", country);
  if (category) params.set("category", category);
  if (month !== undefined) params.set("month", String(month));
  if (q) params.set("q", q);
  if (destination) params.set("destination", destination);

  const paramsString = params.toString();
  const url = paramsString
    ? `/api/festivals/registry?${paramsString}`
    : "/api/festivals/registry";

  // Use a stable query key object for React Query — includes all filter params
  const paramsObject = { country, category, month, q, destination };

  return useQuery<FestivalRegistryEntry[]>({
    queryKey: ["/api/festivals/registry", paramsObject],
    queryFn: async () => {
      // credentials:"include" for stale-session resilience. The endpoint is
      // public (fix #94), but degrade to [] on 401 rather than throwing — a
      // throw here makes the registry data undefined and empties the whole
      // picker universe (the #94 failure chain).
      const res = await fetch(url, { credentials: "include" });
      if (res.status === 401) return [];
      if (!res.ok) {
        const text = await res.text().catch(() => res.statusText);
        throw new Error(`${res.status}: ${text}`);
      }
      return res.json();
    },
    enabled,
    staleTime: 60 * 60 * 1000,    // 1 hour — festival data rarely changes
    gcTime: 4 * 60 * 60 * 1000,   // 4 hours in memory cache
  });
}

/**
 * Fetch a single festival's full config (artists, stages, schedule, etc.).
 *
 * @param festivalId - The festival ID (e.g. "ultra-miami-2026"), or null to disable
 * @returns useQuery result with `data: any` (full FestivalConfig JSON)
 */
export function useFestivalById(festivalId: string | null) {
  return useQuery<FestivalConfig | null>({
    queryKey: ["/api/festivals", festivalId, "detail"],
    queryFn: async () => {
      const res = await fetch(`/api/festivals/${festivalId}/detail`);
      if (!res.ok) {
        const text = await res.text().catch(() => res.statusText);
        throw new Error(`${res.status}: ${text}`);
      }
      return res.json();
    },
    enabled: !!festivalId,
    staleTime: 60 * 60 * 1000,    // 1 hour
    gcTime: 4 * 60 * 60 * 1000,   // 4 hours
  });
}
